import type { DbClient } from "@tanstack/db";
import type { QueryClient } from "@tanstack/query-core";
import { apiPost, apiPut } from "@softmax/observatory-client/client";
import type { PostPublic, VotePublic } from "@softmax/observatory-client/types";
import {
  postDetailCollections,
  postFeedCollection,
  type PostFeedScope,
} from "../post.collections";

export async function refreshPostFeed({
  db,
  scope,
}: {
  db: DbClient;
  scope: PostFeedScope;
}) {
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const source = db.collection(postFeedCollection(scope));
  const queryClient = db.requireDependency<QueryClient>("queryClient");
  const queryKey = ["posts", "feed", scope] as const;
  await queryClient.cancelQueries({ queryKey });
  await queryClient.invalidateQueries({ queryKey, refetchType: "none" });
  signal.throwIfAborted();
  await source.utils.refetch({ throwOnError: true });
}

export async function createForumPost({
  db,
  scope,
  title,
  body,
  format,
  idempotencyKey,
  imageKey,
}: {
  db: DbClient;
  scope: PostFeedScope;
  title: string;
  body: string;
  format: "text" | "markdown";
  idempotencyKey: string;
  imageKey?: string | undefined;
}) {
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const post = await apiPost<PostPublic>(
    `/v2/forums/${encodeURIComponent(scope.forumSlug)}/posts`,
    {
      title,
      body,
      content_format: format,
      idempotency_key: idempotencyKey,
      media: imageKey ? [{ media_type: "image", s3_key: imageKey }] : [],
    },
    { elevatedPrivileges: scope.elevatedPrivileges, signal },
  ).catch((error: Error) => {
    db.requireDependency<(error: Error) => void>("reportAuthorizationError")(
      error,
    );
    throw error;
  });
  signal.throwIfAborted();
  await db.collection(postFeedCollection(scope)).utils.writeUpsert({
    ...post,
    feedPosition: -1,
  });
  signal.throwIfAborted();
  const client = db.requireDependency<QueryClient>("queryClient");
  await client.invalidateQueries({
    queryKey: ["posts", "feed", { forumSlug: scope.forumSlug }],
  });
  return post;
}

export async function voteForPost({
  db,
  postId,
  value,
  elevatedPrivileges,
  feedScope,
  forumSlug,
}: {
  db: DbClient;
  postId: string;
  value: -1 | 0 | 1;
  elevatedPrivileges: boolean;
  feedScope?: PostFeedScope;
  forumSlug: string;
}) {
  const signal = db.requireDependency<AbortController>("lifetime").signal;
  const vote = await apiPut<VotePublic>(
    `/v2/posts/${encodeURIComponent(postId)}/vote`,
    { value },
    { elevatedPrivileges, signal },
  ).catch((error: Error) => {
    db.requireDependency<(error: Error) => void>("reportAuthorizationError")(
      error,
    );
    throw error;
  });
  signal.throwIfAborted();
  const detail = db.collection(
    postDetailCollections({ postId, elevatedPrivileges }).post,
  );

  const detailPost = detail.get(postId);

  if (detailPost) {
    await detail.utils.writeUpdate({
      id: postId,
      score: vote.score,
      vote_count: vote.vote_count ?? detailPost.vote_count,
    });
  }

  signal.throwIfAborted();

  if (feedScope) {
    const feed = db.collection(postFeedCollection(feedScope));

    const feedPost = feed.get(postId);

    if (feedPost) {
      await feed.utils.writeUpdate({
        id: postId,
        score: vote.score,
        vote_count: vote.vote_count ?? feedPost.vote_count,
      });
    }
  }

  signal.throwIfAborted();
  void db.requireDependency<QueryClient>("queryClient").invalidateQueries({
    queryKey: ["posts", "feed", { forumSlug, elevatedPrivileges }],
  });
  return vote;
}
